/**
 * AdminRoles.tsx — zarządzanie rolami ITE (RBAC, Krok 1a.4).
 *
 * Master-detail:
 *  - Lewa kolumna: lista ról (kafelki). Badge „systemowa" dla wbudowanych ról,
 *    licznik przypisanych użytkowników. Przycisk „Nowa rola".
 *  - Prawa kolumna: panel wybranej roli — edycja nazwy/opisu, usuwanie (tylko
 *    role custom), checkbox lista uprawnień pogrupowana po kategorii + zapis.
 *
 * Dostęp gated permission `admin_roles.manage` (backend zwraca 403 — obsługa inline).
 *
 * Data:
 *  - GET /admin/admin-roles                       → lista ról + userCount
 *  - GET /admin/admin-permissions                 → słownik uprawnień
 *  - GET /admin/admin-roles/:id/permissions       → uprawnienia wybranej roli
 *  - POST/PATCH/DELETE /admin/admin-roles[/:id]   → CRUD ról
 *  - PUT /admin/admin-roles/:id/permissions       → replace-all uprawnień
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Layout } from '@/components/Layout';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface AdminRoleRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  sortOrder: number | null;
  userCount: number;
}

interface AdminPermissionRow {
  code: string;
  category: string;
  name: string;
  description: string | null;
  sortOrder: number | null;
}

function extractErrorCode(err: unknown): string | undefined {
  const ax = err as AxiosError<{ error?: string }>;
  return ax.response?.data?.error;
}

export function AdminRoles() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const notifySuccess = (msg: string) => toast.show({ variant: 'success', description: msg });
  const notifyError = (msg: string) => toast.show({ variant: 'error', description: msg });

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminRoleRow | null>(null);

  // Lista ról.
  const rolesQuery = useQuery<{ roles: AdminRoleRow[] }>({
    queryKey: ['admin', 'admin-roles'],
    queryFn: async () => (await api.get('/admin/admin-roles')).data,
    retry: false,
  });

  // Słownik uprawnień (do checkbox list).
  const permissionsQuery = useQuery<{ permissions: AdminPermissionRow[] }>({
    queryKey: ['admin', 'admin-permissions'],
    queryFn: async () => (await api.get('/admin/admin-permissions')).data,
    retry: false,
  });

  // Uprawnienia wybranej roli.
  const rolePermsQuery = useQuery<{ permissionCodes: string[] }>({
    queryKey: ['admin', 'admin-roles', selectedRoleId, 'permissions'],
    queryFn: async () => (await api.get(`/admin/admin-roles/${selectedRoleId}/permissions`)).data,
    enabled: selectedRoleId !== null,
    retry: false,
  });

  // Lokalny draft checkboxów uprawnień (dirty tracking).
  const [draftCodes, setDraftCodes] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (rolePermsQuery.data) {
      setDraftCodes(new Set(rolePermsQuery.data.permissionCodes));
    }
  }, [rolePermsQuery.data]);

  const selectedRole = useMemo(
    () => rolesQuery.data?.roles.find((r) => r.id === selectedRoleId) ?? null,
    [rolesQuery.data, selectedRoleId],
  );

  const isDirty = useMemo(() => {
    if (!rolePermsQuery.data) return false;
    const saved = new Set(rolePermsQuery.data.permissionCodes);
    if (saved.size !== draftCodes.size) return true;
    for (const c of draftCodes) if (!saved.has(c)) return true;
    return false;
  }, [rolePermsQuery.data, draftCodes]);

  // Uprawnienia pogrupowane po kategorii.
  const groupedPermissions = useMemo(() => {
    const groups = new Map<string, AdminPermissionRow[]>();
    for (const p of permissionsQuery.data?.permissions ?? []) {
      const arr = groups.get(p.category) ?? [];
      arr.push(p);
      groups.set(p.category, arr);
    }
    return [...groups.entries()];
  }, [permissionsQuery.data]);

  const createMutation = useMutation({
    mutationFn: async (body: { code: string; name: string; description?: string }) =>
      (await api.post('/admin/admin-roles', body)).data,
    onSuccess: (created: AdminRoleRow) => {
      notifySuccess(t('adminRoles.toast.created'));
      setCreateOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'admin-roles'] });
      setSelectedRoleId(created.id);
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      notifyError(
        code === 'ROLE_CODE_EXISTS'
          ? t('adminRoles.toast.codeExists')
          : t('adminRoles.toast.createError'),
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (body: { name: string; description: string | null }) =>
      (await api.patch(`/admin/admin-roles/${selectedRoleId}`, body)).data,
    onSuccess: () => {
      notifySuccess(t('adminRoles.toast.updated'));
      setEditOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'admin-roles'] });
    },
    onError: () => notifyError(t('adminRoles.toast.updateError')),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/admin/admin-roles/${id}`)).data,
    onSuccess: () => {
      notifySuccess(t('adminRoles.toast.deleted'));
      if (selectedRoleId === pendingDelete?.id) setSelectedRoleId(null);
      setPendingDelete(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'admin-roles'] });
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      notifyError(
        code === 'CANNOT_DELETE_SYSTEM_ROLE'
          ? t('adminRoles.toast.cannotDeleteSystem')
          : t('adminRoles.toast.deleteError'),
      );
      setPendingDelete(null);
    },
  });

  const savePermsMutation = useMutation({
    mutationFn: async () =>
      (
        await api.put(`/admin/admin-roles/${selectedRoleId}/permissions`, {
          permissionCodes: [...draftCodes],
        })
      ).data,
    onSuccess: () => {
      notifySuccess(t('adminRoles.toast.permsSaved'));
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'admin-roles', selectedRoleId, 'permissions'],
      });
    },
    onError: () => notifyError(t('adminRoles.toast.permsError')),
  });

  function togglePermission(code: string) {
    setDraftCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('adminRoles.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('adminRoles.subtitle')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>{t('adminRoles.newRole')}</Button>
      </div>

      {rolesQuery.isError ? (
        <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
          {t('adminRoles.loadError')}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
          {/* Lewa: lista ról */}
          <div className="flex flex-col gap-2">
            {rolesQuery.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              rolesQuery.data?.roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setSelectedRoleId(role.id)}
                  className={[
                    'rounded-lg border p-3 text-left transition',
                    role.id === selectedRoleId
                      ? 'border-accent bg-accent/5'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600',
                  ].join(' ')}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{role.name}</span>
                    {role.isSystem && <Badge color="gray">{t('adminRoles.systemBadge')}</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <code>{role.code}</code> ·{' '}
                    {t('adminRoles.userCount', { count: role.userCount })}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Prawa: panel wybranej roli */}
          <div>
            {!selectedRole ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
                {t('adminRoles.selectHint')}
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 p-5 dark:border-gray-700">
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold">{selectedRole.name}</h2>
                    {selectedRole.description && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {selectedRole.description}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button variant="secondary" onClick={() => setEditOpen(true)}>
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant="danger"
                      disabled={selectedRole.isSystem}
                      title={selectedRole.isSystem ? t('adminRoles.systemNoDelete') : undefined}
                      onClick={() => setPendingDelete(selectedRole)}
                    >
                      {t('common.delete')}
                    </Button>
                  </div>
                </div>

                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t('adminRoles.permissionsHeading')}
                </h3>

                {permissionsQuery.isLoading || rolePermsQuery.isLoading ? (
                  <Skeleton className="h-48 w-full" />
                ) : (
                  <div className="flex flex-col gap-4">
                    {groupedPermissions.map(([category, perms]) => (
                      <div key={category}>
                        <div className="mb-1 text-xs font-medium uppercase text-gray-400">
                          {t(`adminRoles.category.${category}`, category)}
                        </div>
                        <div className="flex flex-col gap-1.5 pl-1">
                          {perms.map((p) => (
                            <Checkbox
                              key={p.code}
                              checked={draftCodes.has(p.code)}
                              onChange={() => togglePermission(p.code)}
                              label={
                                <span>
                                  {p.name}
                                  {p.description && (
                                    <span className="ml-1 text-xs text-gray-400">
                                      — {p.description}
                                    </span>
                                  )}
                                </span>
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ))}

                    <div className="mt-2 flex items-center gap-3">
                      <Button
                        onClick={() => savePermsMutation.mutate()}
                        disabled={!isDirty || savePermsMutation.isPending}
                      >
                        {t('adminRoles.savePermissions')}
                      </Button>
                      {isDirty && (
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          {t('adminRoles.unsavedChanges')}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <CreateRoleModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(body) => createMutation.mutate(body)}
        pending={createMutation.isPending}
      />

      {selectedRole && (
        <EditRoleModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          role={selectedRole}
          onSubmit={(body) => updateMutation.mutate(body)}
          pending={updateMutation.isPending}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('adminRoles.deleteTitle')}
        message={pendingDelete ? t('adminRoles.deleteMessage', { name: pendingDelete.name }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Modale create / edit
// ---------------------------------------------------------------------------

function CreateRoleModal({
  open,
  onClose,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: { code: string; name: string; description?: string }) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      setCode('');
      setName('');
      setDescription('');
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('adminRoles.newRole')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => onSubmit({ code, name, description: description || undefined })}
            disabled={pending || !code || !name}
          >
            {t('common.create')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label={t('adminRoles.field.code')}
          hint={t('adminRoles.field.codeHint')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="np. module_designer"
        />
        <Input
          label={t('adminRoles.field.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('adminRoles.field.namePlaceholder')}
        />
        <Input
          label={t('adminRoles.field.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </Modal>
  );
}

function EditRoleModal({
  open,
  onClose,
  role,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  role: AdminRoleRow;
  onSubmit: (body: { name: string; description: string | null }) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');

  useEffect(() => {
    if (open) {
      setName(role.name);
      setDescription(role.description ?? '');
    }
  }, [open, role]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('adminRoles.editRole')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => onSubmit({ name, description: description || null })}
            disabled={pending || !name}
          >
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Input label={t('adminRoles.field.code')} value={role.code} disabled />
        <Input
          label={t('adminRoles.field.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label={t('adminRoles.field.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </Modal>
  );
}
